import axios from 'axios'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** 为 true 时不弹出全局错误提示（用于可降级的可选接口） */
    silentError?: boolean
  }
}
import { message } from 'ant-design-vue'
import type { RequestUserStore } from './types'
import { parseApiJson } from './parseApiJson'

let getUserStore: (() => RequestUserStore) | null = null

/** 在 main.ts 中 pinia 初始化后绑定，避免 request ↔ user store 循环依赖 */
export function setupRequestStore(storeGetter: () => RequestUserStore) {
  getUserStore = storeGetter
}

function resolveUserStore(): RequestUserStore | null {
  return getUserStore?.() ?? null
}

/** 从后台自定义异常响应中取出可展示文案 */
export function resolveApiErrorMessage(data: unknown, fallback = ''): string {
  if (typeof data === 'string') {
    const text = data.trim()
    if (!text) return fallback
    try {
      return resolveApiErrorMessage(JSON.parse(text), fallback)
    } catch {
      return text
    }
  }
  if (!data || typeof data !== 'object') return fallback

  const body = data as Record<string, unknown>
  const fields = ['message', 'msg', 'errorMsg', 'errorMessage']
  for (const key of fields) {
    const value = body[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  if (typeof body.data === 'string' && body.data.trim()) return body.data.trim()
  return fallback
}

const looksLikeJsonText = (text: string) => {
  const trimmed = text.replace(/^\uFEFF/, '').trim()
  return trimmed.startsWith('{') || trimmed.startsWith('[')
}

const parseJsonIfPossible = (text: string) => {
  const trimmed = text.replace(/^\uFEFF/, '').trim()
  const start = trimmed.indexOf('{')
  if (start < 0) return null
  try {
    return parseApiJson(trimmed.slice(start))
  } catch {
    return null
  }
}

/** 导出接口 responseType=blob 时，把后端业务错误 JSON 从文件流里还原出来 */
async function parseBlobIfJson(data: unknown): Promise<unknown | null> {
  if (typeof data === 'string') {
    return looksLikeJsonText(data) ? parseJsonIfPossible(data) : null
  }
  if (!data || typeof data !== 'object') return null
  if ('code' in data && !('text' in data && typeof (data as Blob).text === 'function')) {
    return data
  }
  if (!('text' in data) || typeof (data as Blob).text !== 'function') return null
  const blob = data as Blob
  if (blob.type && /json/i.test(blob.type) === false && blob.size > 64 * 1024) {
    return null
  }
  return parseJsonIfPossible(await blob.text())
}

/** 从导出结果里取出后端自定义异常文案，例如 31005「无记录可以导出」 */
export async function resolveExportBizError(data: unknown): Promise<string> {
  const parsed = (await parseBlobIfJson(data)) ?? (data && typeof data === 'object' ? data : null)
  if (!parsed || typeof parsed !== 'object') return ''
  const code = String((parsed as { code?: string | number }).code ?? '')
  if (!code || code === '200') return ''
  return resolveApiErrorMessage(parsed)
}

/** 从请求 catch 的 Error / 原始响应中取出可展示文案 */
export function resolveRequestErrorMessage(error: unknown, fallback = '操作失败'): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: unknown } }).response
    const fromResponse = resolveApiErrorMessage(response?.data)
    if (fromResponse) return fromResponse
  }
  if (error instanceof Error && error.message.trim()) return error.message.trim()
  if (typeof error === 'string' && error.trim()) return error.trim()
  return fallback
}

const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL, // 从环境变量读取接口地址
  timeout: 1000 * 30,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // 允许跨域携带cookie
  transformResponse: [
    (data) => {
      if (typeof data !== 'string') return data
      const text = data.trim()
      if (!text) return data
      try {
        return parseApiJson(text)
      } catch {
        return data
      }
    },
  ],
})

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    const userStore = resolveUserStore()
    if (config.url === '/user/login') {
      return config
    }
    if (!userStore) {
      return config
    }
    if (userStore.userInfo.tokenValue) {
      // token存在就携带token
      config.headers[userStore.userInfo.tokenName] = `${userStore.userInfo.tokenValue}`
    } else {
      userStore.logout()
      // return Promise.reject(new Error('未授权，请先登录'))
    }

    // 添加时间戳防止缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        // _t: Date.now(),
      }
    }

    return config
  },
  (error) => {
    console.error('请求错误：', error)
    return Promise.reject(error)
  }
)

const handleBizResponse = (
  data: { code?: string | number } & Record<string, unknown>,
  silentError = false
) => {
  const userStore = resolveUserStore()

  if ([200, '200'].includes(data.code as string | number)) {
    return data
  }

  let errorMsg: string = resolveApiErrorMessage(data)
  const code = String(data.code ?? '')

  if (['11011', '11012', '11016'].includes(code)) {
    errorMsg = ''
    userStore?.logout()
  } else if (code === '60001') {
    errorMsg = ''
  } else if (code === '500') {
    errorMsg = resolveApiErrorMessage(data, '服务异常，请联系管理员')
  }

  if (errorMsg && !silentError) {
    message.error(errorMsg)
  }

  const rejectError = new Error(errorMsg || '操作失败') as Error & { response?: { data: unknown } }
  rejectError.response = { data }
  return Promise.reject(rejectError)
}

// 响应拦截器
service.interceptors.response.use(
  async (response) => {
    const { data, config } = response

    if (config.responseType === 'blob') {
      const parsed = await parseBlobIfJson(data)
      if (parsed && typeof parsed === 'object' && parsed !== null && 'code' in parsed) {
        return handleBizResponse(parsed as { code?: string | number } & Record<string, unknown>)
      }
      return { code: '200', data, message: '', dateTime: '', traceId: '' }
    }

    return handleBizResponse(data, Boolean(config.silentError))
  },
  async (error) => {
    const userStore = resolveUserStore()
    const silentError = Boolean(error.config?.silentError)
    let errorMessage: string = ''
    if (error.response) {
      const { status, data } = error.response
      const parsed = (await parseBlobIfJson(data)) ?? data
      console.error(`❌ 服务器响应错误 ${status}: 🚀 ${error.config.url}`, parsed)

      errorMessage = resolveApiErrorMessage(parsed, '服务异常，请联系管理员')
      if (status === 400) {
        if (['11016'].includes((parsed as { code?: string })?.code || '')) {
          userStore?.logout()
        }
      }
    } else if (error.request) {
      console.error(`❌ 网络错误 🚀 ${error.message}`)
      errorMessage = '网络异常，请检查网络'
    } else {
      console.error('❌ 请求错误 🚀', error.message)
      errorMessage = `${error.message || '请求异常，请联系管理员'}`
    }

    if (errorMessage && !silentError) {
      message.error(errorMessage)
    }

    const rejectError = new Error(errorMessage || '请求异常') as Error & { response?: { data: unknown } }
    if (error.response) {
      rejectError.response = { data: error.response.data }
    }
    return Promise.reject(rejectError)
  }
)

export default service
